import { isBoosterEnrolled } from 'helpers/sesameCash.helpers';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLogger } from 'hook/error.hook';
import { getBuilderState } from 'pages/Cash/pages/BuilderHome/builder.helper';
import {
  getBankingConfig,
  getBuilderCardSettings,
  getBuilderRecentTransactions,
  getBuilderStatementMetadata,
} from 'actions/cashBuilder.actions';
import { CLICK } from 'externals/_tracking/types/eventTypes.constants';
import { useHistory } from 'react-router-dom';
import { useEventHandler } from 'hook/event.hook';
import { isNully } from 'helpers/objectHelpers';
import { BANKING_VERTICAL } from 'types/mrph.vertical.constants';

export const useBuilderManager = c => {
  const eventHandler = useEventHandler();
  const { userInfo, sesameCashAccount } = c;
  const history = useHistory();
  const dispatch = useDispatch();
  const logger = useLogger({ name: 'BuilderManager.hook' });
  if (!c?.sesameCashState) throw new Error('Requires a sesameCashState');
  const doRedirect = id => {
    logger.debug(`Redirecting user out of builder page ${id}`);
    history.push('/cash');
  };
  useEffect(() => {
    if (userInfo && sesameCashAccount && c.builderAccountRequired) {
      if (!sesameCashAccount?.securedCard?.hasCard) doRedirect('hasBuilder');
    }
    //eslint-disable-next-line
  }, [userInfo, sesameCashAccount]);
  const {
    allCardDetails,
    allCardDetailsLoading,
    allCardDetailsError,
    recentSecuredCardStatements,
    recentSecuredCardStatementsLoading,
    recentSecuredCardStatementsError,
    securedCardSettings,
    securedCardSettingsLoading,
    securedCardSettingsError,
    recentBuilderTransaction,
    recentBuilderTransactionLoading,
    recentBuilderTransactionError,
    bankingConfig,
    bankingConfigLoading,
    bankingConfigError,
  } = useSelector(state => ({
    allCardDetails: state.sesameCash.allCardDetails,
    allCardDetailsLoading: state.sesameCash.allCardDetailsLoading,
    allCardDetailsError: state.sesameCash.allCardDetailsError,
    recentSecuredCardStatements: state.sesameCash.recentSecuredCardStatements,
    recentSecuredCardStatementsLoading:
      state.sesameCash.recentSecuredCardStatementsLoading,
    recentSecuredCardStatementsError:
      state.sesameCash.recentSecuredCardStatementsError,
    securedCardSettings: state.sesameCash.securedCardSettings,
    securedCardSettingsLoading: state.sesameCash.securedCardSettingsLoading,
    securedCardSettingsError: state.sesameCash.securedCardSettingsError,
    bankingConfig: state.sesameCash.bankingConfig,
    bankingConfigLoading: state.sesameCash.bankingConfigLoading,
    bankingConfigError: state.sesameCash.bankingConfigError,
    recentBuilderTransaction: state.sesameCash.recentBuilderTransaction,
    recentBuilderTransactionLoading:
      state.sesameCash.recentBuilderTransactionLoading,
    recentBuilderTransactionError:
      state.sesameCash.recentBuilderTransactionError,
  }));

  const onSeeAllTransactionClick = () => {
    eventHandler(CLICK, {
      'Click Type': 'View All Transactions',
      Vertical: BANKING_VERTICAL,
    });
    history.push('/cash/builder/transaction');
  };
  const onRefreshTransactionsClick = () => {
    dispatch(getBuilderRecentTransactions());
  };

  useEffect(() => {
    if (
      isBoosterEnrolled(sesameCashAccount) &&
      c?.sesameCashState?.cashPinToken &&
      !recentSecuredCardStatements &&
      !recentSecuredCardStatementsLoading &&
      !recentSecuredCardStatementsError
    ) {
      dispatch(getBuilderStatementMetadata(c?.sesameCashState?.cashPinToken));
    }
    //eslint-disable-next-line
  }, [
    sesameCashAccount,
    c?.sesameCashState?.cashPinToken,
    recentSecuredCardStatements,
    recentSecuredCardStatementsLoading,
    recentSecuredCardStatementsError,
  ]);

  useEffect(() => {
    if (
      isBoosterEnrolled(sesameCashAccount) &&
      c?.sesameCashState?.cashPinToken &&
      !securedCardSettings &&
      !securedCardSettingsLoading &&
      !securedCardSettingsError
    ) {
      dispatch(getBuilderCardSettings());
    }
    //eslint-disable-next-line
  }, [
    sesameCashAccount,
    c?.sesameCashState?.cashPinToken,
    securedCardSettings,
    securedCardSettingsLoading,
    securedCardSettingsError,
  ]);

  useEffect(() => {
    if (
      isBoosterEnrolled(sesameCashAccount) &&
      c?.sesameCashState?.cashPinToken &&
      (!recentBuilderTransaction ||
        isNully(recentBuilderTransaction?.length)) &&
      !recentBuilderTransactionLoading &&
      !recentBuilderTransactionError
    ) {
      dispatch(getBuilderRecentTransactions());
    }
    //eslint-disable-next-line
  }, [
    sesameCashAccount,
    c?.sesameCashState?.cashPinToken,
    recentBuilderTransaction,
    recentBuilderTransactionLoading,
    recentBuilderTransactionError,
  ]);

  useEffect(() => {
    if (
      isBoosterEnrolled(sesameCashAccount) &&
      c?.sesameCashState?.cashPinToken &&
      !bankingConfig &&
      !bankingConfigLoading &&
      !bankingConfigError
    ) {
      dispatch(getBankingConfig());
    }
    //eslint-disable-next-line
  }, [
    sesameCashAccount,
    c?.sesameCashState?.cashPinToken,
    bankingConfig,
    bankingConfigLoading,
    bankingConfigError,
  ]);

  let calculatedState = null;

  if (
    allCardDetails &&
    recentSecuredCardStatements &&
    securedCardSettings &&
    bankingConfig &&
    sesameCashAccount
  ) {
    calculatedState = getBuilderState({
      allCardDetails,
      recentSecuredCardStatements,
      securedCardSettings,
      bankingConfig,
      sesameCashAccount,
    });
  }

  const onClickContinueBuildingCredit = () => {
    history.push('/cash/builder/utilizationManagement');
  };

  const onClickPaymentHeaderCard = () => {
    if (calculatedState?.activityBarLink) {
      c.history.push(calculatedState?.activityBarLink);
    }
  };

  return {
    loading:
      allCardDetailsLoading ||
      recentSecuredCardStatementsLoading ||
      recentBuilderTransactionLoading ||
      securedCardSettingsLoading ||
      bankingConfigLoading,
    isEnrolledInBuilder: isBoosterEnrolled(sesameCashAccount),
    isFunded: sesameCashAccount?.receivedFirstFund,
    error: allCardDetailsError || recentSecuredCardStatementsError,
    ...calculatedState,
    recentBuilderTransaction,
    recentBuilderTransactionError,
    onSeeAllTransactionClick,
    onRefreshTransactionsClick,
    onClickPaymentHeaderCard,
    onClickContinueBuildingCredit,
    recentSecuredCardStatements,
    securedCardSettings,
    securedCardSettingsLoading,
  };
};
